import QuillMadeArticle from "components/Article/QuillMade";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import _ from "lodash";
import { staffClient } from "clients/staff";
import { FileUtils } from "components/Utils/file";
import prettyBytes from "pretty-bytes";
import { toast } from "react-toastify";
import PageContainer from "ui/PageContainer";
import PageHeader from "ui/PageHeader";
import Button from "ui/Button";
import { Badge } from "ui/shadcn";
import moment from "moment";
import Lightbox from "yet-another-react-lightbox";
import { Counter, Captions, Download } from "yet-another-react-lightbox/plugins";
import { ArrowDownTrayIcon, ArrowLeftIcon, DocumentIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";
import "yet-another-react-lightbox/plugins/captions.css";

export const ArticleViewer = () => {
    const [files, setFiles] = useState([]);
    const [boardName, setBoardName] = useState("");
    const [boardSiteId, setBoardSiteId] = useState<number | null>(null);
    const [writer, setWriter] = useState("");
    const [writerId, setWriterId] = useState<number | null>(null);
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("");
    const [contents, setContents] = useState({});
    const [createdAt, setCreatedAt] = useState("");
    const [updatedAt, setUpdatedAt] = useState("");
    const [isPublic, setIsPublic] = useState(true);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [currentUserId, setCurrentUserId] = useState<number | null>(null);
    const [currentUserType, setCurrentUserType] = useState<string>("");

    const params = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const canMutate = currentUserType === 'MASTER' || (writerId !== null && currentUserId === writerId);

    const isImageFile = (fileName: string) => {
        const ext = fileName.split('.').pop()?.toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(ext || '');
    };

    const imageFiles = files.filter(file => isImageFile(file.name));
    const otherFiles = files.filter(file => !isImageFile(file.name));

    const lightboxSlides = imageFiles.map(file => ({
        src: file.url,
        alt: file.name,
        description: file.name,
        download: file.url
    }));

    useEffect(() => {
        updateData();
    }, []);

    const updateData = async () => {
        await fetchCurrentUser();
        if (params.articleId) {
            await updateArticle();
        }
    }

    const fetchCurrentUser = async () => {
        try {
            const ret = await staffClient.get('/auth/my-info');
            setCurrentUserId(ret.data?.token?.id ?? null);
            setCurrentUserType(ret.data?.token?.type ?? '');
        } catch (e) {
            console.error('[ArticleViewer] my-info failed:', e);
        }
    }

    const updateArticle = async () => {
        const ret = await staffClient.get(`/article/${params.articleId}`);
        setBoardName(ret.data?.board?.name);
        setBoardSiteId(ret.data?.board?.site_id ?? ret.data?.board?.site?.id ?? null);
        setWriter(ret.data?.writer?.name);
        setWriterId(ret.data?.writer_id ?? ret.data?.writer?.id ?? null);
        setTitle(ret.data.title);
        setContents(ret.data.contents);
        setCategory(ret.data?.category?.name);
        setFiles(ret.data.files);
        setCreatedAt(ret.data?.created_at);
        setUpdatedAt(ret.data?.updated_at);
        setIsPublic(ret.data?.is_public ?? true);
    }

    /**
     * 첨부파일 다운로드 이력 기록 — 보안 로그(누가 어떤 파일을 받았는지)용.
     * 기록이 실패해도 다운로드 자체는 막지 않는다 (에러는 콘솔만).
     */
    const logDownload = async (file: { name?: string; url?: string }) => {
        if (!file) return;
        try {
            const querySiteId = Number(searchParams.get('siteId'));
            const resolvedSiteId =
                Number.isFinite(querySiteId) && querySiteId > 0 ? querySiteId : boardSiteId;
            await staffClient.post('/download-log', {
                fileName: file.name,
                fileUrl: file.url,
                siteId: resolvedSiteId ?? undefined,
                boardName: boardName || undefined,
                categoryName: category && category !== '없음' ? category : undefined,
            });
        } catch (e) {
            console.error('[ArticleViewer] 다운로드 로그 기록 실패:', e);
        }
    };

    const handleFileDownload = async (file: { name?: string; url?: string }) => {
        if (!file?.url) {
            toast.error('파일 URL이 유효하지 않습니다');
            return;
        }
        await logDownload(file);
        await FileUtils.downloadFile(file.url, file.name || '');
    };

    const handleDelete = async () => {
        try {
            const ok = window.confirm("게시글을 삭제하시겠습니까?");
            if (!ok) return;
            await staffClient.delete(`/article/${params.articleId}`);
            toast.success("게시글이 삭제되었습니다");
            navigate(-1)
        } catch (e) {
            toast.error("게시글 삭제에 실패했습니다")
        }
    }

    const from = searchParams.get('from');
    const siteId = searchParams.get('siteId');
    const wasEdited = updatedAt && createdAt && moment(updatedAt).format('YYYY-MM-DD HH:mm:ss') !== moment(createdAt).format('YYYY-MM-DD HH:mm:ss');

    return (
        <PageContainer>
            <PageHeader
                title="게시글 보기"
                breadcrumb={
                    from === 'site' && siteId
                        ? [
                            { label: boardName || '게시판', path: `/sites/${siteId}?tab=board` },
                            { label: '게시글' }
                        ]
                        : [
                            { label: boardName || '게시판', path: `/board/${params.boardId}` },
                            { label: '게시글' }
                        ]
                }
                actions={
                    canMutate ? (
                        <div className="flex gap-2">
                            <Button variant="danger-outline" onClick={handleDelete}>
                                삭제
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    const queryString = from === 'site' && siteId ? `?from=site&siteId=${siteId}` : '';
                                    navigate(`/boards/${params.boardId}/articles/editor/${params.articleId}${queryString}`);
                                }}
                            >
                                수정
                            </Button>
                        </div>
                    ) : null
                }
            />

            {/* 게시글 = 객체 1개 = 단일 카드 (헤더 / 본문 / 첨부파일 섹션) */}
            <article className="rounded border border-border bg-card overflow-hidden">
                {/* 헤더 — 배지 / 제목 / 메타 */}
                <header className="px-8 pt-8 pb-6 border-b border-border">
                    <div className="flex flex-wrap gap-1.5 mb-3">
                        {boardName && <Badge variant="default">{boardName}</Badge>}
                        {category && category !== "없음" && (
                            <Badge variant="outline">{category}</Badge>
                        )}
                        {isPublic ? (
                            <Badge variant="success">공개</Badge>
                        ) : (
                            <Badge variant="secondary">비공개</Badge>
                        )}
                    </div>

                    <h1 className="text-2xl font-bold text-foreground leading-tight tracking-tight">
                        {title || "제목 없음"}
                    </h1>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mt-3">
                        {writer && <span className="font-medium text-neutral-700">{writer}</span>}
                        {writer && createdAt && <span className="text-neutral-300">·</span>}
                        {createdAt && <span className="tabular-nums">{moment(createdAt).format('YYYY.MM.DD HH:mm')}</span>}
                        {wasEdited && (
                            <>
                                <span className="text-neutral-300">·</span>
                                <span className="text-neutral-400 tabular-nums">수정됨 {moment(updatedAt).format('YYYY.MM.DD HH:mm')}</span>
                            </>
                        )}
                    </div>
                </header>

                {/* 본문 */}
                <div className="px-8 py-8">
                    <QuillMadeArticle contents={contents} />
                </div>

                {/* 첨부파일 */}
                {!_.isEmpty(files) && (
                    <div className="px-8 py-6 border-t border-border bg-neutral-50">
                        <h3 className="text-sm font-semibold text-neutral-700 mb-4 flex items-center gap-1.5">
                            <PaperClipIcon className="w-3.5 h-3.5" strokeWidth={2} />
                            첨부파일
                        </h3>

                        {!_.isEmpty(imageFiles) && (
                            <div className={otherFiles.length > 0 ? "mb-5" : ""}>
                                <p className="text-xs text-muted-foreground mb-2">이미지 {imageFiles.length}</p>
                                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                                    {imageFiles.map((file, index) => (
                                        <div
                                            key={index}
                                            className="aspect-square rounded overflow-hidden cursor-pointer bg-neutral-100 border border-border hover:border-navy-700 transition-colors duration-fast"
                                            onClick={() => {
                                                setLightboxIndex(index);
                                                setLightboxOpen(true);
                                            }}
                                        >
                                            <img
                                                src={file.url}
                                                alt={file.name}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {!_.isEmpty(otherFiles) && (
                            <div>
                                <p className="text-xs text-muted-foreground mb-2">파일 {otherFiles.length}</p>
                                <ul className="flex flex-col gap-1">
                                    {otherFiles.map((file, index) => (
                                        <li key={index}>
                                            <button
                                                type="button"
                                                onClick={() => handleFileDownload(file)}
                                                className="flex w-full items-center gap-2 rounded border border-border bg-card px-3 py-2 text-left transition-colors duration-fast hover:bg-navy-50"
                                                title={`${file.name} 다운로드`}
                                            >
                                                <DocumentIcon className="h-4 w-4 shrink-0 text-neutral-400" />
                                                <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                                                    {file.name}
                                                </span>
                                                <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                                                    {prettyBytes(Number(file.size) || 0)}
                                                </span>
                                                <ArrowDownTrayIcon className="h-4 w-4 shrink-0 text-neutral-400" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {/* 하단 액션 */}
                <footer className="px-8 py-3 border-t border-border">
                    <Button variant="ghost" size="small" icon={<ArrowLeftIcon />} onClick={() => navigate(-1)}>
                        목록으로
                    </Button>
                </footer>
            </article>

            {/* Lightbox */}
            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={lightboxSlides}
                index={lightboxIndex}
                plugins={[Counter, Captions, Download]}
                on={{
                    // 이미지 첨부 다운로드도 로그로 남긴다
                    download: ({ index }) => {
                        void logDownload(imageFiles[index]);
                    },
                }}
            />
        </PageContainer>
    );
};

export default ArticleViewer;
